import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
    GraduationCap, Plus, Users, Target, Trophy, Coins,
    ClipboardText, CaretRight, Trash, Copy, MagnifyingGlass,
    BookOpen, Calendar, CheckCircle, X, UserPlus, ArrowLeft,
    PencilSimple, Archive, ArrowCounterClockwise, Eye, EyeSlash,
    Funnel, GameController, Leaf, Gear
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

function ActivityPackCard({ pack, onAssign, submitting }) {
    const [expanded, setExpanded] = useState(false);
    const [viewingMission, setViewingMission] = useState(null);
    const [viewingQuiz, setViewingQuiz] = useState(null);

    return (
        <div className="brutalist-card p-4">
            {/* Pack Header */}
            <div
                className="flex justify-between items-start cursor-pointer"
                onClick={() => setExpanded(!expanded)}
                data-testid={`pack-header-${pack.id}`}
            >
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h4 className="font-fredoka font-bold text-[#1e293b] text-lg">{pack.title}</h4>
                        <CaretRight
                            weight="bold"
                            size={18}
                            className={`text-[#94a3b8] transition-transform ${expanded ? 'rotate-90' : ''}`}
                        />
                    </div>
                    <p className="text-sm text-[#475569] mt-1">{pack.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                        {pack.curriculum_tags?.map(tag => (
                            <span key={tag} className="text-xs bg-[#38bdf8]/10 text-[#38bdf8] px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                        {pack.year_levels?.map(yl => (
                            <span key={yl} className="text-xs bg-[#22c55e]/10 text-[#22c55e] px-2 py-0.5 rounded-full">Year {yl}</span>
                        ))}
                    </div>
                    <p className="text-xs text-[#94a3b8] mt-2">
                        {pack.missions?.length || 0} missions · {pack.quizzes?.length || 0} quizzes
                    </p>
                </div>
                <motion.button
                    whileHover={{ y: -2, x: -2 }}
                    whileTap={{ y: 2, x: 2 }}
                    onClick={(e) => { e.stopPropagation(); onAssign(); }}
                    disabled={submitting}
                    className="brutalist-btn bg-[#22c55e] text-white text-sm disabled:opacity-50 ml-3 shrink-0"
                    data-testid={`assign-pack-${pack.id}`}
                >
                    Assign
                </motion.button>
            </div>

            {/* Expanded Content */}
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="mt-4 pt-4 border-t-2 border-[#e2e8f0] space-y-3">
                            {/* Missions */}
                            {pack.missions?.length > 0 && (
                                <div>
                                    <h5 className="font-fredoka font-semibold text-[#1e293b] mb-2 flex items-center gap-1.5">
                                        <Target weight="duotone" size={18} className="text-[#22c55e]" />
                                        Missions
                                    </h5>
                                    <div className="space-y-2">
                                        {pack.missions.map(m => (
                                            <div key={m.id}>
                                                <div
                                                    className="flex items-center justify-between p-3 bg-[#f8fafc] rounded-xl border border-[#e2e8f0] cursor-pointer hover:border-[#22c55e] transition-colors"
                                                    onClick={() => setViewingMission(viewingMission?.id === m.id ? null : m)}
                                                    data-testid={`mission-item-${m.id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-[#22c55e]/10 flex items-center justify-center">
                                                            <Target weight="bold" size={16} className="text-[#22c55e]" />
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-[#1e293b] text-sm">{m.title}</p>
                                                            <div className="flex gap-2 mt-0.5">
                                                                <span className="text-xs text-[#f59e0b] font-semibold">+{m.xp_reward} Tokens</span>
                                                                <span className="text-xs text-[#94a3b8] capitalize">{m.difficulty}</span>
                                                                {m.ecosystem && <span className="text-xs text-[#94a3b8] capitalize">{m.ecosystem}</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <CaretRight
                                                        weight="bold"
                                                        size={16}
                                                        className={`text-[#94a3b8] transition-transform ${viewingMission?.id === m.id ? 'rotate-90' : ''}`}
                                                    />
                                                </div>
                                                {/* Mission Detail */}
                                                <AnimatePresence>
                                                    {viewingMission?.id === m.id && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="ml-11 mt-2 p-3 bg-white rounded-xl border border-[#e2e8f0] space-y-2">
                                                                {m.description && (
                                                                    <p className="text-sm text-[#475569]">{m.description}</p>
                                                                )}
                                                                {m.instructions?.length > 0 && (
                                                                    <div>
                                                                        <p className="text-xs font-semibold text-[#1e293b] mb-1">Steps:</p>
                                                                        <ol className="list-decimal list-inside text-sm text-[#475569] space-y-0.5">
                                                                            {m.instructions.map((step, i) => (
                                                                                <li key={i}>{step}</li>
                                                                            ))}
                                                                        </ol>
                                                                    </div>
                                                                )}
                                                                {m.curriculum_tags?.length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 pt-1">
                                                                        {m.curriculum_tags.map(tag => (
                                                                            <span key={tag} className="text-xs bg-[#38bdf8]/10 text-[#38bdf8] px-2 py-0.5 rounded-full">{tag}</span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quizzes */}
                            {pack.quizzes?.length > 0 && (
                                <div>
                                    <h5 className="font-fredoka font-semibold text-[#1e293b] mb-2 flex items-center gap-1.5">
                                        <ClipboardText weight="duotone" size={18} className="text-[#a855f7]" />
                                        Quizzes
                                    </h5>
                                    <div className="space-y-2">
                                        {pack.quizzes.map(q => (
                                            <div key={q.id}>
                                                <div
                                                    className="flex items-center justify-between p-3 bg-[#f8fafc] rounded-xl border border-[#e2e8f0] cursor-pointer hover:border-[#a855f7] transition-colors"
                                                    onClick={() => setViewingQuiz(viewingQuiz?.id === q.id ? null : q)}
                                                    data-testid={`quiz-item-${q.id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-[#a855f7]/10 flex items-center justify-center">
                                                            <ClipboardText weight="bold" size={16} className="text-[#a855f7]" />
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-[#1e293b] text-sm">{q.title}</p>
                                                            <div className="flex gap-2 mt-0.5">
                                                                <span className="text-xs text-[#f59e0b] font-semibold">+{q.xp_reward} Tokens</span>
                                                                <span className="text-xs text-[#94a3b8]">{q.questions?.length || 0} questions</span>
                                                                {q.difficulty && <span className="text-xs text-[#94a3b8] capitalize">{q.difficulty}</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <CaretRight
                                                        weight="bold"
                                                        size={16}
                                                        className={`text-[#94a3b8] transition-transform ${viewingQuiz?.id === q.id ? 'rotate-90' : ''}`}
                                                    />
                                                </div>
                                                {/* Quiz Detail */}
                                                <AnimatePresence>
                                                    {viewingQuiz?.id === q.id && q.questions?.length > 0 && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="ml-11 mt-2 p-3 bg-white rounded-xl border border-[#e2e8f0] space-y-3">
                                                                {q.topic && (
                                                                    <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Topic: {q.topic}</p>
                                                                )}
                                                                {q.questions.map((question, qi) => (
                                                                    <div key={qi} className="text-sm">
                                                                        <p className="font-semibold text-[#1e293b]">
                                                                            {qi + 1}. {question.question}
                                                                        </p>
                                                                        <ul className="mt-1 space-y-0.5 ml-4">
                                                                            {question.options?.map((opt, oi) => (
                                                                                <li
                                                                                    key={oi}
                                                                                    className={`text-sm ${opt === question.correct_answer ? 'text-[#22c55e] font-semibold' : 'text-[#475569]'}`}
                                                                                >
                                                                                    {String.fromCharCode(65 + oi)}) {opt}
                                                                                    {opt === question.correct_answer && ' ✓'}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function EducatorDashboard() {
    const { user, api } = useAuth();
    const [school, setSchool] = useState(null);
    const [classrooms, setClassrooms] = useState([]);
    const [selectedClassroom, setSelectedClassroom] = useState(null);
    const [classroomDetail, setClassroomDetail] = useState(null);
    const [activityPacks, setActivityPacks] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modals
    const [showCreateSchool, setShowCreateSchool] = useState(false);
    const [showCreateClassroom, setShowCreateClassroom] = useState(false);
    const [showAssignMissions, setShowAssignMissions] = useState(false);
    const [showBulkCreate, setShowBulkCreate] = useState(false);
    const [showActivityPacks, setShowActivityPacks] = useState(false);
    const [showActionsMenu, setShowActionsMenu] = useState(false);
    const [showEditSchool, setShowEditSchool] = useState(false);
    const [showAddEducator, setShowAddEducator] = useState(false);
    const [showAllStudents, setShowAllStudents] = useState(false);
    const [showClassroomFilter, setShowClassroomFilter] = useState('active');

    // Form state
    const [schoolName, setSchoolName] = useState('');
    const [schoolState, setSchoolState] = useState('NSW');
    const [schoolType, setSchoolType] = useState('public');
    const [classroomName, setClassroomName] = useState('');
    const [bulkStudents, setBulkStudents] = useState([{ name: '', age: 10 }]);

    // Edit school state
    const [editSchoolName, setEditSchoolName] = useState('');
    const [editSchoolState, setEditSchoolState] = useState('');
    const [editSchoolType, setEditSchoolType] = useState('');

    // Add educator state
    const [newEducatorName, setNewEducatorName] = useState('');
    const [newEducatorEmail, setNewEducatorEmail] = useState('');
    const [newEducatorPassword, setNewEducatorPassword] = useState('');
    const [addTeacherTab, setAddTeacherTab] = useState('linked'); // 'linked' | 'new'
    const [linkedParents, setLinkedParents] = useState([]);
    const [addingParentId, setAddingParentId] = useState(null);

    // School-wide data
    const [allStudents, setAllStudents] = useState([]);
    const [educators, setEducators] = useState([]);

    // Missions for assignment
    const [availableMissions, setAvailableMissions] = useState([]);
    const [selectedMissionIds, setSelectedMissionIds] = useState([]);
    const [missionSearch, setMissionSearch] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [activeTab, setActiveTab] = useState('overview');

    const [submitting, setSubmitting] = useState(false);

    const loadData = useCallback(async () => {
        try {
            const [schoolRes, classroomsRes] = await Promise.all([
                api().get('/educator/school'),
                api().get('/educator/classrooms').catch(() => ({ data: [] }))
            ]);
            setSchool(schoolRes.data);
            setClassrooms(classroomsRes.data || []);
            if (!schoolRes.data) setShowCreateSchool(true);
        } catch (error) {
            console.error('Error loading educator data:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const loadClassroomDetail = useCallback(async (classroomId) => {
        try {
            const res = await api().get(`/educator/classroom/${classroomId}`);
            setClassroomDetail(res.data);
        } catch (error) {
            toast.error('Failed to load classroom');
        }
    }, [api]);

    useEffect(() => {
        if (selectedClassroom) {
            loadClassroomDetail(selectedClassroom.id);
        }
    }, [selectedClassroom, loadClassroomDetail]);

    const handleCreateSchool = async (e) => {
        e.preventDefault();
        if (!schoolName) { toast.error('Please enter a school name'); return; }
        setSubmitting(true);
        try {
            const res = await api().post('/educator/school', {
                name: schoolName, state: schoolState, school_type: schoolType
            });
            toast.success('School created!');
            setSchool({ id: res.data.id, name: schoolName, state: schoolState, school_type: schoolType });
            setShowCreateSchool(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create school');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateClassroom = async (e) => {
        e.preventDefault();
        if (!classroomName) { toast.error('Please enter a classroom name'); return; }
        setSubmitting(true);
        try {
            const res = await api().post('/educator/classroom', { name: classroomName });
            toast.success(`Classroom created! Code: ${res.data.class_code}`);
            setClassrooms(prev => [...prev, { ...res.data, student_count: 0, assignment_count: 0 }]);
            setShowCreateClassroom(false);
            setClassroomName('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create classroom');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBulkCreate = async (e) => {
        e.preventDefault();
        const valid = bulkStudents.filter(s => s.name.trim());
        if (!valid.length) { toast.error('Add at least one student'); return; }
        setSubmitting(true);
        try {
            const res = await api().post(`/educator/classroom/${selectedClassroom.id}/bulk-create`, {
                students: valid
            });
            toast.success(res.data.message);
            setShowBulkCreate(false);
            setBulkStudents([{ name: '', age: 10 }]);
            loadClassroomDetail(selectedClassroom.id);
            loadData();
        } catch (error) {
            toast.error('Failed to create students');
        } finally {
            setSubmitting(false);
        }
    };

    const loadMissions = async () => {
        try {
            const res = await api().get('/missions');
            setAvailableMissions(res.data);
        } catch (error) {
            console.error('Failed to load missions');
        }
    };

    const handleAssignMissions = async () => {
        if (!selectedMissionIds.length) { toast.error('Select at least one mission'); return; }
        setSubmitting(true);
        try {
            const res = await api().post(`/educator/classroom/${selectedClassroom.id}/assign`, {
                mission_ids: selectedMissionIds,
                due_date: dueDate || null
            });
            toast.success(res.data.message);
            setShowAssignMissions(false);
            setSelectedMissionIds([]);
            setDueDate('');
            loadClassroomDetail(selectedClassroom.id);
        } catch (error) {
            toast.error('Failed to assign missions');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAssignPack = async (packId) => {
        setSubmitting(true);
        try {
            const res = await api().post(`/educator/classroom/${selectedClassroom.id}/assign-pack/${packId}`);
            toast.success(res.data.message);
            loadClassroomDetail(selectedClassroom.id);
        } catch (error) {
            toast.error('Failed to assign activity pack');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveStudent = async (studentId) => {
        if (!window.confirm('Remove this student from the classroom?')) return;
        try {
            await api().delete(`/educator/classroom/${selectedClassroom.id}/student/${studentId}`);
            toast.success('Student removed');
            loadClassroomDetail(selectedClassroom.id);
        } catch (error) {
            toast.error('Failed to remove student');
        }
    };

    const handleUnassignMission = async (missionId) => {
        try {
            await api().post(`/educator/classroom/${selectedClassroom.id}/unassign/${missionId}`);
            toast.success('Mission unassigned');
            loadClassroomDetail(selectedClassroom.id);
        } catch (error) {
            toast.error('Failed to unassign mission');
        }
    };

    const loadActivityPacks = async () => {
        try {
            const res = await api().get('/educator/activity-packs');
            setActivityPacks(res.data);
        } catch (error) {
            console.error('Failed to load packs');
        }
    };

    const handleEditSchool = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await api().put('/educator/school', {
                name: editSchoolName || undefined,
                state: editSchoolState || undefined,
                school_type: editSchoolType || undefined
            });
            setSchool(res.data);
            setShowEditSchool(false);
            toast.success('School updated');
        } catch (error) {
            toast.error('Failed to update school');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddEducator = async (e) => {
        e.preventDefault();
        if (!newEducatorName || !newEducatorEmail || !newEducatorPassword) {
            toast.error('Please fill in all fields'); return;
        }
        setSubmitting(true);
        try {
            const res = await api().post('/educator/school/add-educator', {
                name: newEducatorName, email: newEducatorEmail, password: newEducatorPassword
            });
            toast.success(`${newEducatorName} added as educator`);
            setEducators(prev => [...prev, res.data.educator]);
            setShowAddEducator(false);
            setNewEducatorName(''); setNewEducatorEmail(''); setNewEducatorPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add educator');
        } finally {
            setSubmitting(false);
        }
    };

    const loadLinkedParents = async () => {
        try {
            const res = await api().get('/auth/linked-parents');
            setLinkedParents(res.data);
        } catch (err) {
            setLinkedParents([]);
        }
    };

    const handleAddParentAsEducator = async (parentId, parentName) => {
        setAddingParentId(parentId);
        try {
            const res = await api().post('/educator/school/add-parent-educator', { parent_id: parentId });
            toast.success(res.data.message);
            setEducators(prev => [...prev, res.data.educator]);
            setLinkedParents(prev => prev.filter(p => p.id !== parentId));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add parent');
        } finally {
            setAddingParentId(null);
        }
    };

    const openAddTeacherModal = () => {
        loadEducators();
        loadLinkedParents();
        setAddTeacherTab('linked');
        setShowAddEducator(true);
    };

    const handleCloseClassroom = async (classroomId) => {
        if (!window.confirm('Close this classroom? Students will no longer see it.')) return;
        try {
            await api().post(`/educator/classroom/${classroomId}/close`);
            setClassrooms(prev => prev.map(c => c.id === classroomId ? { ...c, status: 'closed' } : c));
            toast.success('Classroom closed');
        } catch (error) {
            toast.error('Failed to close classroom');
        }
    };

    const handleReopenClassroom = async (classroomId) => {
        try {
            await api().post(`/educator/classroom/${classroomId}/reopen`);
            setClassrooms(prev => prev.map(c => c.id === classroomId ? { ...c, status: 'active' } : c));
            toast.success('Classroom reopened');
        } catch (error) {
            toast.error('Failed to reopen classroom');
        }
    };

    const loadAllStudents = async () => {
        try {
            const res = await api().get('/educator/school/all-students');
            setAllStudents(res.data);
        } catch (error) {
            console.error('Failed to load students');
        }
    };

    const loadEducators = async () => {
        try {
            const res = await api().get('/educator/school/educators');
            setEducators(res.data);
        } catch (error) {
            console.error('Failed to load educators');
        }
    };

    const openEditSchool = () => {
        if (school) {
            setEditSchoolName(school.name || '');
            setEditSchoolState(school.state || 'NSW');
            setEditSchoolType(school.school_type || 'public');
        }
        setShowEditSchool(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // Classroom detail view
    if (selectedClassroom && classroomDetail) {
        const { classroom, students, assignments } = classroomDetail;
        const totalTokens = students.reduce((sum, s) => sum + (s.xp || 0), 0);
        const totalMissions = students.reduce((sum, s) => sum + (s.completed_missions?.length || 0), 0);

        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
                    <button
                        onClick={() => { setSelectedClassroom(null); setClassroomDetail(null); }}
                        className="flex items-center gap-2 text-[#475569] hover:text-[#1e293b] mb-4 font-semibold"
                        data-testid="back-to-classrooms"
                    >
                        <ArrowLeft weight="bold" size={20} /> Back to Classrooms
                    </button>

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                        <div>
                            <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                                {classroom.name}
                            </h1>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-[#475569]">Class Code:</span>
                                <span className="font-mono font-bold text-[#22c55e] bg-[#22c55e]/10 px-3 py-1 rounded-lg border-2 border-[#1e293b]" data-testid="class-code">
                                    {classroom.class_code}
                                </span>
                                <button
                                    onClick={() => { navigator.clipboard.writeText(classroom.class_code); toast.success('Copied!'); }}
                                    className="p-1 hover:bg-[#f1f5f9] rounded"
                                >
                                    <Copy weight="bold" size={16} />
                                </button>
                            </div>
                        </div>
                        <div className="relative self-start">
                            <motion.button
                                whileHover={{ y: -2, x: -2 }}
                                whileTap={{ y: 2, x: 2 }}
                                onClick={() => setShowActionsMenu(!showActionsMenu)}
                                className="brutalist-btn bg-[#22c55e] text-white flex items-center gap-2 text-sm"
                                data-testid="actions-menu-btn"
                            >
                                <Plus weight="bold" size={18} /> Actions
                            </motion.button>
                            {showActionsMenu && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowActionsMenu(false)} />
                                    <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="absolute left-0 sm:right-0 sm:left-auto top-full mt-2 z-50 brutalist-card p-2 w-56 bg-white"
                                    >
                                        <button
                                            onClick={() => { setShowActionsMenu(false); setShowBulkCreate(true); }}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#38bdf8]/10 text-left transition-colors"
                                            data-testid="bulk-create-btn"
                                        >
                                            <UserPlus weight="bold" size={20} className="text-[#38bdf8]" />
                                            <span className="font-semibold text-[#1e293b] text-sm">Add Students</span>
                                        </button>
                                        <button
                                            onClick={() => { setShowActionsMenu(false); loadMissions(); setShowAssignMissions(true); }}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#22c55e]/10 text-left transition-colors"
                                            data-testid="assign-missions-btn"
                                        >
                                            <Target weight="bold" size={20} className="text-[#22c55e]" />
                                            <span className="font-semibold text-[#1e293b] text-sm">Assign Missions</span>
                                        </button>
                                        <button
                                            onClick={() => { setShowActionsMenu(false); loadActivityPacks(); setShowActivityPacks(true); }}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#f59e0b]/10 text-left transition-colors"
                                            data-testid="activity-packs-btn"
                                        >
                                            <BookOpen weight="bold" size={20} className="text-[#f59e0b]" />
                                            <span className="font-semibold text-[#1e293b] text-sm">Activity Packs</span>
                                        </button>
                                    </motion.div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {[
                            { label: 'Students', value: students.length, icon: Users, color: '#38bdf8' },
                            { label: 'Bush Tokens', value: totalTokens, icon: Coins, color: '#f59e0b' },
                            { label: 'Missions Done', value: totalMissions, icon: CheckCircle, color: '#22c55e' },
                            { label: 'Assigned', value: assignments.length, icon: ClipboardText, color: '#a855f7' }
                        ].map((stat) => (
                            <div key={stat.label} className="brutalist-card p-4">
                                <stat.icon weight="duotone" size={24} style={{ color: stat.color }} />
                                <p className="font-fredoka text-2xl font-bold text-[#1e293b] mt-1">{stat.value}</p>
                                <p className="text-sm text-[#475569]">{stat.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mb-4 border-b-2 border-[#1e293b]/10">
                        {['students', 'assignments'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 font-fredoka font-semibold capitalize transition-colors ${
                                    activeTab === tab
                                        ? 'text-[#22c55e] border-b-3 border-[#22c55e]'
                                        : 'text-[#475569] hover:text-[#1e293b]'
                                }`}
                                data-testid={`tab-${tab}`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Students Tab */}
                    {activeTab === 'students' && (
                        <div className="brutalist-card overflow-hidden">
                            {students.length === 0 ? (
                                <div className="p-8 text-center">
                                    <Users weight="duotone" size={48} className="text-[#94a3b8] mx-auto mb-3" />
                                    <p className="text-[#475569] mb-2">No students yet</p>
                                    <p className="text-sm text-[#94a3b8]">Add students or share the class code: <strong>{classroom.class_code}</strong></p>
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-[#f8fafc] border-b-2 border-[#1e293b]/10">
                                            <th className="px-4 py-3 text-left font-fredoka">Name</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Level</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Tokens</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Missions</th>
                                            <th className="px-4 py-3 text-left font-fredoka">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {students.map(s => (
                                            <tr key={s.id} className="border-b border-[#f1f5f9]">
                                                <td className="px-4 py-3 font-semibold">{s.name}</td>
                                                <td className="px-4 py-3 text-sm">{s.level || 'Rookie Ranger'}</td>
                                                <td className="px-4 py-3">
                                                    <span className="font-bold text-[#f59e0b]">{s.xp || 0}</span>
                                                </td>
                                                <td className="px-4 py-3">{s.completed_missions?.length || 0}</td>
                                                <td className="px-4 py-3">
                                                    <button
                                                        onClick={() => handleRemoveStudent(s.id)}
                                                        className="p-2 rounded-lg hover:bg-red-100 text-red-500"
                                                        data-testid={`remove-student-${s.id}`}
                                                    >
                                                        <Trash weight="bold" size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {/* Assignments Tab */}
                    {activeTab === 'assignments' && (
                        <div className="space-y-3">
                            {assignments.length === 0 ? (
                                <div className="brutalist-card p-8 text-center">
                                    <Target weight="duotone" size={48} className="text-[#94a3b8] mx-auto mb-3" />
                                    <p className="text-[#475569]">No missions assigned yet</p>
                                </div>
                            ) : (
                                assignments.map(a => (
                                    <div key={a.mission_id} className="brutalist-card p-4 flex items-center justify-between">
                                        <div>
                                            <p className="font-fredoka font-bold text-[#1e293b]">{a.mission_title}</p>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-sm text-[#22c55e] font-semibold">
                                                    {a.completed_by.length}/{a.total_students} completed
                                                </span>
                                                {a.due_date && (
                                                    <span className="text-sm text-[#475569] flex items-center gap-1">
                                                        <Calendar size={14} /> Due: {new Date(a.due_date).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Progress bar */}
                                            <div className="w-48 h-2 bg-[#e2e8f0] rounded-full mt-2">
                                                <div
                                                    className="h-full bg-[#22c55e] rounded-full transition-all"
                                                    style={{ width: `${a.total_students ? (a.completed_by.length / a.total_students * 100) : 0}%` }}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleUnassignMission(a.mission_id)}
                                            className="p-2 rounded-lg hover:bg-red-100 text-red-400"
                                            title="Unassign"
                                        >
                                            <X weight="bold" size={18} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* Bulk Create Students Modal */}
                    {showBulkCreate && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="brutalist-card p-6 max-w-lg w-full bg-white max-h-[80vh] overflow-y-auto"
                            >
                                <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">Add Students</h3>
                                <form onSubmit={handleBulkCreate} className="space-y-3">
                                    {bulkStudents.map((s, i) => (
                                        <div key={i} className="flex gap-2 items-center">
                                            <input
                                                type="text"
                                                value={s.name}
                                                onChange={(e) => {
                                                    const updated = [...bulkStudents];
                                                    updated[i].name = e.target.value;
                                                    setBulkStudents(updated);
                                                }}
                                                placeholder="Student name"
                                                className="brutalist-input flex-1"
                                                data-testid={`student-name-${i}`}
                                            />
                                            <input
                                                type="number"
                                                value={s.age}
                                                onChange={(e) => {
                                                    const updated = [...bulkStudents];
                                                    updated[i].age = parseInt(e.target.value) || 10;
                                                    setBulkStudents(updated);
                                                }}
                                                className="brutalist-input w-20"
                                                min={4} max={18}
                                                placeholder="Age"
                                            />
                                            {bulkStudents.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setBulkStudents(bulkStudents.filter((_, j) => j !== i))}
                                                    className="p-2 text-red-400 hover:text-red-600"
                                                >
                                                    <X weight="bold" size={18} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setBulkStudents([...bulkStudents, { name: '', age: 10 }])}
                                        className="text-[#22c55e] font-semibold text-sm flex items-center gap-1"
                                    >
                                        <Plus weight="bold" size={16} /> Add another
                                    </button>
                                    <div className="flex gap-3 pt-3">
                                        <button type="button" onClick={() => setShowBulkCreate(false)} className="brutalist-btn flex-1 bg-white text-[#1e293b]">Cancel</button>
                                        <button type="submit" disabled={submitting} className="brutalist-btn flex-1 bg-[#38bdf8] text-white disabled:opacity-50">
                                            {submitting ? 'Creating...' : 'Create Students'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </div>
                    )}

                    {/* Assign Missions Modal */}
                    {showAssignMissions && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="brutalist-card p-6 max-w-2xl w-full bg-white max-h-[80vh] overflow-y-auto"
                            >
                                <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">Assign Missions</h3>
                                
                                <div className="relative mb-4">
                                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" size={20} />
                                    <input
                                        type="text"
                                        value={missionSearch}
                                        onChange={(e) => setMissionSearch(e.target.value)}
                                        placeholder="Search missions..."
                                        className="brutalist-input w-full pl-10"
                                        data-testid="mission-search"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-[#1e293b] mb-1">Due Date (optional)</label>
                                    <input
                                        type="date"
                                        value={dueDate}
                                        onChange={(e) => setDueDate(e.target.value)}
                                        className="brutalist-input"
                                    />
                                </div>

                                <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                                    {availableMissions
                                        .filter(m => m.title.toLowerCase().includes(missionSearch.toLowerCase()))
                                        .map(m => (
                                            <label
                                                key={m.id}
                                                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                                                    selectedMissionIds.includes(m.id)
                                                        ? 'border-[#22c55e] bg-[#22c55e]/5'
                                                        : 'border-[#e2e8f0] hover:border-[#94a3b8]'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedMissionIds.includes(m.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedMissionIds(prev => [...prev, m.id]);
                                                        } else {
                                                            setSelectedMissionIds(prev => prev.filter(id => id !== m.id));
                                                        }
                                                    }}
                                                    className="w-5 h-5 rounded"
                                                />
                                                <div className="flex-1">
                                                    <p className="font-semibold text-[#1e293b]">{m.title}</p>
                                                    <div className="flex gap-2 mt-1">
                                                        <span className="text-xs bg-[#f59e0b]/10 text-[#f59e0b] px-2 py-0.5 rounded-full font-semibold">
                                                            +{m.xp_reward} Tokens
                                                        </span>
                                                        <span className="text-xs bg-[#e2e8f0] px-2 py-0.5 rounded-full capitalize">{m.difficulty}</span>
                                                        {m.curriculum_tags?.map(tag => (
                                                            <span key={tag} className="text-xs bg-[#38bdf8]/10 text-[#38bdf8] px-2 py-0.5 rounded-full">{tag}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                </div>

                                <div className="flex gap-3">
                                    <button onClick={() => { setShowAssignMissions(false); setSelectedMissionIds([]); }} className="brutalist-btn flex-1 bg-white text-[#1e293b]">Cancel</button>
                                    <button
                                        onClick={handleAssignMissions}
                                        disabled={submitting || !selectedMissionIds.length}
                                        className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50"
                                        data-testid="confirm-assign"
                                    >
                                        {submitting ? 'Assigning...' : `Assign ${selectedMissionIds.length} Mission(s)`}
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {/* Activity Packs Modal */}
                    {showActivityPacks && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="brutalist-card p-6 max-w-3xl w-full bg-white max-h-[85vh] overflow-y-auto"
                            >
                                <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">Activity Packs</h3>
                                <p className="text-[#475569] mb-4">Pre-built sets of missions and quizzes aligned to the Australian Curriculum. Click a pack to see what's inside.</p>
                                
                                <div className="space-y-4">
                                    {activityPacks.map(pack => (
                                        <ActivityPackCard
                                            key={pack.id}
                                            pack={pack}
                                            onAssign={() => handleAssignPack(pack.id)}
                                            submitting={submitting}
                                        />
                                    ))}
                                    {activityPacks.length === 0 && (
                                        <p className="text-center text-[#475569] py-4">No activity packs available yet.</p>
                                    )}
                                </div>
                                
                                <button onClick={() => setShowActivityPacks(false)} className="brutalist-btn w-full bg-white text-[#1e293b] mt-4">Close</button>
                            </motion.div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Main educator dashboard (school + classrooms overview)
    return (
        <div className="min-h-screen bg-[#fefce8] bg-[radial-gradient(#cbd5e1_1.5px,transparent_1.5px)] [background-size:24px_24px]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#0f172a]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#0f172a] flex items-center justify-center">
                                <GraduationCap weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#0f172a] hidden sm:block">
                                Educator Portal
                            </span>
                        </Link>

                        <div className="flex items-center gap-3">
                            <Link
                                to="/dashboard"
                                className="bg-[#fde047] text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] active:translate-y-0 active:translate-x-0 active:shadow-[2px_2px_0px_#0f172a] transition-all"
                                data-testid="parent-portal-link"
                            >
                                <Users weight="bold" size={18} />
                                <span className="hidden sm:inline">Parent Portal</span>
                            </Link>
                            <span className="text-[#334155] font-bold hidden sm:block">
                                {user?.name}
                            </span>
                            <Link
                                to="/account"
                                className="relative p-2 rounded-full hover:bg-gray-100"
                                data-testid="account-settings-link"
                                title="Account Settings"
                            >
                                <Gear weight="bold" size={22} className="text-[#0f172a]" />
                                {user?.email_verified === false && (
                                    <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[#ef4444] border-2 border-white" data-testid="account-unverified-dot" />
                                )}
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            <div className="max-w-6xl mx-auto px-6 md:px-12 pt-28 pb-24">
                {/* School Info Banner */}
                {school && (
                    <div className="bg-green-300 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-3xl p-8 mb-10 flex flex-col md:flex-row justify-between items-start md:items-center relative overflow-hidden" data-testid="school-banner">
                        <div className="z-10">
                            <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#0f172a] leading-tight">{school.name}</h1>
                            <p className="text-lg text-[#0f172a]/80 font-bold mt-2 flex items-center gap-2">
                                <GraduationCap weight="bold" size={20} />
                                {school.state} · <span className="capitalize">{school.school_type}</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-6 md:mt-0 z-10">
                            <button
                                onClick={openEditSchool}
                                className="bg-white text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                                data-testid="edit-school-btn"
                            >
                                <PencilSimple weight="bold" size={16} /> Edit School
                            </button>
                            <button
                                onClick={openAddTeacherModal}
                                className="bg-white text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                                data-testid="add-educator-btn"
                            >
                                <UserPlus weight="bold" size={16} /> Add Teacher
                            </button>
                        </div>
                        {/* Decorative shape */}
                        <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-green-400 rounded-full border-4 border-[#0f172a] opacity-40 z-0" />
                    </div>
                )}

                {/* Bento Stats Grid */}
                {classrooms.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-5 mb-12">
                        {/* Active Classrooms */}
                        <div className="md:col-span-2 bg-blue-200 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 flex flex-col justify-between">
                            <GraduationCap weight="duotone" size={40} className="text-[#0f172a]/20" />
                            <div className="mt-4">
                                <p className="font-fredoka text-4xl font-bold text-[#0f172a]">{classrooms.filter(c => c.status !== 'closed').length}</p>
                                <p className="text-sm font-bold text-[#0f172a]/70 uppercase tracking-wide mt-1">Active Classrooms</p>
                            </div>
                        </div>
                        {/* Total Students */}
                        <div
                            className="md:col-span-2 bg-purple-200 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 flex flex-col justify-between cursor-pointer hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all"
                            onClick={() => { loadAllStudents(); setShowAllStudents(true); }}
                            data-testid="total-students-card"
                        >
                            <Users weight="duotone" size={40} className="text-[#0f172a]/20" />
                            <div className="mt-4">
                                <p className="font-fredoka text-4xl font-bold text-[#0f172a]">
                                    {classrooms.reduce((sum, c) => sum + (c.student_count || 0), 0)}
                                </p>
                                <p className="text-sm font-bold text-[#0f172a]/70 uppercase tracking-wide mt-1">Total Students →</p>
                            </div>
                        </div>
                        {/* Active Assignments */}
                        <div className="col-span-2 bg-yellow-300 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 flex flex-col justify-between">
                            <Target weight="duotone" size={40} className="text-[#0f172a]/20" />
                            <div className="mt-4">
                                <p className="font-fredoka text-4xl font-bold text-[#0f172a]">
                                    {classrooms.reduce((sum, c) => sum + (c.assignment_count || 0), 0)}
                                </p>
                                <p className="text-sm font-bold text-[#0f172a]/70 uppercase tracking-wide mt-1">Active Assignments</p>
                            </div>
                        </div>
                        {/* Worksheets CTA */}
                        <Link to="/worksheets" className="col-span-1 md:col-span-2 bg-orange-200 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 flex items-center justify-between cursor-pointer hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group" data-testid="worksheets-link">
                            <div className="flex items-center gap-4">
                                <BookOpen weight="duotone" size={36} className="text-[#0f172a]/60" />
                                <div>
                                    <p className="font-fredoka text-xl font-bold text-[#0f172a]">Worksheets</p>
                                    <p className="text-sm font-semibold text-[#0f172a]/60">Print & Generate</p>
                                </div>
                            </div>
                            <CaretRight weight="bold" size={24} className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        {/* Games CTA */}
                        <Link to="/games" className="col-span-1 md:col-span-2 bg-pink-200 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 flex items-center justify-between cursor-pointer hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group" data-testid="games-link">
                            <div className="flex items-center gap-4">
                                <GameController weight="duotone" size={36} className="text-[#0f172a]/60" />
                                <div>
                                    <p className="font-fredoka text-xl font-bold text-[#0f172a]">Games</p>
                                    <p className="text-sm font-semibold text-[#0f172a]/60">Learn & Play</p>
                                </div>
                            </div>
                            <CaretRight weight="bold" size={24} className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        {/* Habitats CTA */}
                        <Link to="/habitats" className="col-span-2 md:col-span-2 bg-green-200 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 flex items-center justify-between cursor-pointer hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group" data-testid="habitats-link">
                            <div className="flex items-center gap-4">
                                <Leaf weight="duotone" size={36} className="text-[#0f172a]/60" />
                                <div>
                                    <p className="font-fredoka text-xl font-bold text-[#0f172a]">Habitats</p>
                                    <p className="text-sm font-semibold text-[#0f172a]/60">Explore Ecosystems</p>
                                </div>
                            </div>
                            <CaretRight weight="bold" size={24} className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                )}

                {/* Classrooms Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
                    <div className="flex items-center gap-4">
                        <h2 className="font-fredoka text-2xl font-bold text-[#0f172a]">Classrooms</h2>
                        <div className="flex border-4 border-[#0f172a] p-1 rounded-2xl bg-white shadow-[4px_4px_0px_#0f172a]">
                            <button
                                onClick={() => setShowClassroomFilter('active')}
                                className={`px-5 py-2 rounded-xl text-sm font-bold transition-colors ${showClassroomFilter === 'active' ? 'bg-[#0f172a] text-white' : 'text-[#475569] hover:text-[#0f172a] hover:bg-slate-100'}`}
                                data-testid="filter-active"
                            >
                                Active
                            </button>
                            <button
                                onClick={() => setShowClassroomFilter('closed')}
                                className={`px-5 py-2 rounded-xl text-sm font-bold transition-colors ${showClassroomFilter === 'closed' ? 'bg-[#0f172a] text-white' : 'text-[#475569] hover:text-[#0f172a] hover:bg-slate-100'}`}
                                data-testid="filter-closed"
                            >
                                Closed
                            </button>
                        </div>
                    </div>
                    <motion.button
                        whileHover={{ y: -2, x: -2 }}
                        whileTap={{ y: 2, x: 2 }}
                        onClick={() => setShowCreateClassroom(true)}
                        className="bg-[#22c55e] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-6 py-3 flex items-center gap-2 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                        data-testid="create-classroom-btn"
                    >
                        <Plus weight="bold" size={18} /> New Classroom
                    </motion.button>
                </div>

                {(() => {
                    const filtered = classrooms.filter(c => (c.status || 'active') === showClassroomFilter);
                    return filtered.length === 0 ? (
                        <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-12 text-center">
                            {showClassroomFilter === 'closed' ? (
                                <>
                                    <Archive weight="duotone" size={56} className="text-[#94a3b8] mx-auto mb-3" />
                                    <p className="text-[#475569] font-semibold">No closed classrooms</p>
                                </>
                            ) : (
                                <>
                                    <GraduationCap weight="duotone" size={56} className="text-[#94a3b8] mx-auto mb-3" />
                                    <p className="text-[#475569] font-semibold mb-1">No classrooms yet</p>
                                    <p className="text-sm text-[#94a3b8]">Create your first classroom to get started</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filtered.map(c => (
                                <div key={c.id} className={`bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 flex flex-col gap-4 relative overflow-hidden group hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all ${c.status === 'closed' ? 'opacity-60' : ''}`}>
                                    {/* Card Header */}
                                    <div className="flex items-start justify-between border-b-4 border-[#0f172a] pb-4">
                                        <div
                                            className="flex-1 cursor-pointer"
                                            onClick={() => c.status !== 'closed' && setSelectedClassroom(c)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-fredoka text-lg font-bold text-[#0f172a]">{c.name}</h3>
                                                {c.status === 'closed' && (
                                                    <span className="text-xs bg-[#94a3b8]/20 text-[#475569] px-2 py-0.5 rounded-full font-bold">Closed</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {c.status === 'closed' ? (
                                                <button
                                                    onClick={() => handleReopenClassroom(c.id)}
                                                    className="p-1.5 rounded-lg hover:bg-[#22c55e]/10 text-[#22c55e]"
                                                    title="Reopen"
                                                    data-testid={`reopen-classroom-${c.id}`}
                                                >
                                                    <ArrowCounterClockwise weight="bold" size={18} />
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => setSelectedClassroom(c)}
                                                        className="p-1.5 rounded-lg hover:bg-slate-100 text-[#94a3b8]"
                                                    >
                                                        <CaretRight weight="bold" size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleCloseClassroom(c.id)}
                                                        className="p-1.5 rounded-lg hover:bg-red-100 text-[#94a3b8] hover:text-red-500"
                                                        title="Close classroom"
                                                        data-testid={`close-classroom-${c.id}`}
                                                    >
                                                        <Archive weight="bold" size={18} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {/* Card Stats */}
                                    <div
                                        className={`${c.status !== 'closed' ? 'cursor-pointer' : ''}`}
                                        onClick={() => c.status !== 'closed' && setSelectedClassroom(c)}
                                    >
                                        <div className="flex justify-between items-center bg-slate-50 border-4 border-[#0f172a] rounded-xl p-3">
                                            <span className="flex items-center gap-1.5 text-sm font-bold text-[#0f172a]">
                                                <Users size={16} /> {c.student_count} students
                                            </span>
                                            <span className="flex items-center gap-1.5 text-sm font-bold text-[#0f172a]">
                                                <Target size={16} /> {c.assignment_count} assigned
                                            </span>
                                        </div>
                                        <div className="mt-3 flex items-center gap-2">
                                            <span className="text-xs font-bold text-[#94a3b8] uppercase tracking-wide">Code:</span>
                                            <span className="font-mono text-sm font-bold text-[#22c55e]">{c.class_code}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })()}
            </div>

            {/* Create School Modal */}
            {showCreateSchool && !school && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 md:p-8 max-w-md w-full bg-white"
                    >
                        <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Set Up Your School</h2>
                        <p className="text-[#475569] text-sm mb-6">Create a school to start managing classrooms and students.</p>
                        
                        <form onSubmit={handleCreateSchool} className="space-y-4">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">School Name</label>
                                <input
                                    type="text"
                                    value={schoolName}
                                    onChange={(e) => setSchoolName(e.target.value)}
                                    className="brutalist-input w-full"
                                    placeholder="e.g. Sunrise Primary School"
                                    data-testid="school-name-input"
                                />
                            </div>
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">State</label>
                                <select
                                    value={schoolState}
                                    onChange={(e) => setSchoolState(e.target.value)}
                                    className="brutalist-input w-full"
                                    data-testid="school-state-select"
                                >
                                    {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Type</label>
                                <select
                                    value={schoolType}
                                    onChange={(e) => setSchoolType(e.target.value)}
                                    className="brutalist-input w-full"
                                    data-testid="school-type-select"
                                >
                                    <option value="public">Public School</option>
                                    <option value="private">Private School</option>
                                    <option value="homeschool">Homeschool</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Link to="/dashboard" className="brutalist-btn flex-1 bg-white text-[#1e293b] text-center">Cancel</Link>
                                <button type="submit" disabled={submitting} className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50" data-testid="create-school-submit">
                                    {submitting ? 'Creating...' : 'Create School'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Create Classroom Modal */}
            {showCreateClassroom && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 max-w-md w-full bg-white"
                    >
                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">New Classroom</h3>
                        <form onSubmit={handleCreateClassroom} className="space-y-4">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Classroom Name</label>
                                <input
                                    type="text"
                                    value={classroomName}
                                    onChange={(e) => setClassroomName(e.target.value)}
                                    className="brutalist-input w-full"
                                    placeholder="e.g. Year 4 Blue"
                                    data-testid="classroom-name-input"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setShowCreateClassroom(false)} className="brutalist-btn flex-1 bg-white text-[#1e293b]">Cancel</button>
                                <button type="submit" disabled={submitting} className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50" data-testid="create-classroom-submit">
                                    {submitting ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Edit School Modal */}
            {showEditSchool && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 max-w-md w-full bg-white"
                    >
                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">Edit School</h3>
                        <form onSubmit={handleEditSchool} className="space-y-4">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">School Name</label>
                                <input
                                    type="text"
                                    value={editSchoolName}
                                    onChange={(e) => setEditSchoolName(e.target.value)}
                                    className="brutalist-input w-full"
                                    data-testid="edit-school-name"
                                />
                            </div>
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">State</label>
                                <select value={editSchoolState} onChange={(e) => setEditSchoolState(e.target.value)} className="brutalist-input w-full">
                                    {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Type</label>
                                <select value={editSchoolType} onChange={(e) => setEditSchoolType(e.target.value)} className="brutalist-input w-full">
                                    <option value="public">Public School</option>
                                    <option value="private">Private School</option>
                                    <option value="homeschool">Homeschool</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setShowEditSchool(false)} className="brutalist-btn flex-1 bg-white text-[#1e293b]">Cancel</button>
                                <button type="submit" disabled={submitting} className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50" data-testid="save-school-btn">
                                    {submitting ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Add Educator Modal */}
            {showAddEducator && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 max-w-md w-full bg-white"
                    >
                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-2">Add Teacher</h3>

                        {/* Tabs */}
                        <div className="flex border-4 border-[#0f172a] p-1 rounded-2xl bg-white shadow-[4px_4px_0px_#0f172a] mb-4">
                            <button
                                onClick={() => setAddTeacherTab('linked')}
                                className={`flex-1 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${addTeacherTab === 'linked' ? 'bg-[#0f172a] text-white' : 'text-[#475569] hover:text-[#0f172a]'}`}
                                data-testid="tab-linked-parents"
                            >
                                Linked Parents
                            </button>
                            <button
                                onClick={() => setAddTeacherTab('new')}
                                className={`flex-1 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${addTeacherTab === 'new' ? 'bg-[#0f172a] text-white' : 'text-[#475569] hover:text-[#0f172a]'}`}
                                data-testid="tab-new-teacher"
                            >
                                New Teacher
                            </button>
                        </div>

                        {/* Current Teachers */}
                        {educators.length > 0 && (
                            <div className="mb-4 p-3 bg-[#f8fafc] rounded-xl">
                                <p className="text-xs font-semibold text-[#94a3b8] mb-2">Current Teachers</p>
                                {educators.map(ed => (
                                    <div key={ed.id} className="flex items-center gap-2 py-1">
                                        <GraduationCap size={16} className="text-[#22c55e]" />
                                        <span className="text-sm text-[#1e293b] font-semibold">{ed.name}</span>
                                        <span className="text-xs text-[#94a3b8]">{ed.email}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Linked Parents Tab */}
                        {addTeacherTab === 'linked' && (
                            <div>
                                <p className="text-sm text-[#475569] mb-4">Add a linked parent as a teacher — they'll use their existing login.</p>
                                {linkedParents.length > 0 ? (
                                    <div className="space-y-2 mb-4">
                                        {linkedParents.map(p => {
                                            const alreadyAdded = educators.some(ed => ed.id === p.id);
                                            return (
                                                <div key={p.id} className="flex items-center justify-between p-3 bg-white border-2 border-[#e2e8f0] rounded-xl" data-testid={`linked-parent-${p.id}`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-purple-200 border-2 border-[#0f172a] flex items-center justify-center">
                                                            <Users weight="bold" size={16} className="text-[#0f172a]" />
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-sm text-[#0f172a]">{p.name}</p>
                                                            <p className="text-xs text-[#475569]">{p.email}</p>
                                                        </div>
                                                    </div>
                                                    {alreadyAdded ? (
                                                        <span className="text-xs font-bold text-[#22c55e] bg-[#22c55e]/10 px-3 py-1 rounded-full">Added</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleAddParentAsEducator(p.id, p.name)}
                                                            disabled={addingParentId === p.id}
                                                            className="bg-[#22c55e] text-white border-2 border-[#0f172a] shadow-[2px_2px_0px_#0f172a] rounded-lg font-bold px-3 py-1.5 text-sm disabled:opacity-50 hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_#0f172a] transition-all"
                                                            data-testid={`add-parent-educator-${p.id}`}
                                                        >
                                                            {addingParentId === p.id ? 'Adding...' : 'Add'}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center p-6 bg-[#f8fafc] rounded-xl mb-4">
                                        <p className="text-sm text-[#475569] font-semibold">No linked parents found.</p>
                                        <p className="text-xs text-[#94a3b8] mt-1">Invite a parent from the Parent Portal first, or use the "New Teacher" tab.</p>
                                    </div>
                                )}
                                <button onClick={() => setShowAddEducator(false)} className="brutalist-btn w-full bg-white text-[#1e293b]" data-testid="close-add-teacher">Done</button>
                            </div>
                        )}

                        {/* New Teacher Tab */}
                        {addTeacherTab === 'new' && (
                            <div>
                                <p className="text-sm text-[#475569] mb-4">Create a new account for a teacher. They'll set their own password on first login.</p>
                                <form onSubmit={handleAddEducator} className="space-y-4">
                                    <div>
                                        <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Name</label>
                                        <input type="text" value={newEducatorName} onChange={(e) => setNewEducatorName(e.target.value)}
                                            className="brutalist-input w-full" placeholder="Teacher's name" data-testid="educator-name-input" />
                                    </div>
                                    <div>
                                        <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Email</label>
                                        <input type="email" value={newEducatorEmail} onChange={(e) => setNewEducatorEmail(e.target.value)}
                                            className="brutalist-input w-full" placeholder="teacher@school.edu.au" data-testid="educator-email-input" />
                                    </div>
                                    <div>
                                        <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Temporary Password</label>
                                        <input type="text" value={newEducatorPassword} onChange={(e) => setNewEducatorPassword(e.target.value)}
                                            className="brutalist-input w-full" placeholder="At least 6 characters" data-testid="educator-password-input" />
                                    </div>
                                    <div className="flex gap-3">
                                        <button type="button" onClick={() => setShowAddEducator(false)} className="brutalist-btn flex-1 bg-white text-[#1e293b]">Cancel</button>
                                        <button type="submit" disabled={submitting} className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50" data-testid="submit-add-educator">
                                            {submitting ? 'Adding...' : 'Add Teacher'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </motion.div>
                </div>
            )}

            {/* All Students Modal */}
            {showAllStudents && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 max-w-3xl w-full bg-white max-h-[85vh] overflow-y-auto"
                    >
                        <h3 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4">All Students</h3>
                        {allStudents.length === 0 ? (
                            <div className="text-center py-8">
                                <Users weight="duotone" size={48} className="text-[#94a3b8] mx-auto mb-3" />
                                <p className="text-[#475569]">No students enrolled yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-[#f8fafc] border-b-2 border-[#1e293b]/10">
                                            <th className="px-4 py-3 text-left font-fredoka text-sm">Name</th>
                                            <th className="px-4 py-3 text-left font-fredoka text-sm">Classroom</th>
                                            <th className="px-4 py-3 text-left font-fredoka text-sm">Level</th>
                                            <th className="px-4 py-3 text-left font-fredoka text-sm">Tokens</th>
                                            <th className="px-4 py-3 text-left font-fredoka text-sm">Missions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allStudents.map(s => (
                                            <tr key={s.id} className="border-b border-[#f1f5f9]">
                                                <td className="px-4 py-3 font-semibold text-sm">{s.name}</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <span className={s.classroom_status === 'closed' ? 'text-[#94a3b8] line-through' : 'text-[#475569]'}>
                                                        {s.classroom_name}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-[#475569]">{s.level}</td>
                                                <td className="px-4 py-3 text-sm font-bold text-[#f59e0b]">{s.xp}</td>
                                                <td className="px-4 py-3 text-sm">{s.missions_completed}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <button onClick={() => setShowAllStudents(false)} className="brutalist-btn w-full bg-white text-[#1e293b] mt-4">Close</button>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
